"use client";

import { type FC, type MouseEvent, useCallback, useEffect, useRef } from "react";

import DOMPurify from "isomorphic-dompurify";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Button } from "@jti/core-components/components/Button";
import type { FeedbackTranslations } from "@jti/fe-common/config";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ContentFeedbackStatus } from "@/constants/feedback/contentFeedbackStatus";
import type { VideoFeedbackAction } from "@/hooks/feedback/useVideoFeedback";
import { useVideoFeedback } from "@/hooks/feedback/useVideoFeedback";

import styles from "./VideoFeedback.module.scss";

const sanitize = DOMPurify.sanitize;

const TITLE_ID = "video-feedback-title";

interface VideoFeedbackProps {
  videoUrl: string;
  feedbackTranslations: FeedbackTranslations;
  authRegisterPageUrl: string;
  handleClose: () => void;
}

const QuestionWithActions: FC<{ question?: string; actions: VideoFeedbackAction[] }> = ({ question, actions }) => (
  <div className={styles.content}>
    <p className={styles.title} id={TITLE_ID}>
      {question}
    </p>
    <div className={styles.actions}>
      {actions.map(({ label, color = "secondary", href, onClick }) => (
        <Button key={label} variant={color} size="sm" href={href} onClick={href ? undefined : onClick}>
          {label}
        </Button>
      ))}
    </div>
  </div>
);

export const VideoFeedback: FC<VideoFeedbackProps> = ({
  videoUrl,
  feedbackTranslations,
  authRegisterPageUrl,
  handleClose,
}) => {
  const { __ } = useTranslations();
  const { status, isCheckingUser, isInitializing, question, message, actions, submittedMessage, submittedRewardHtml } =
    useVideoFeedback({
      videoUrl,
      feedbackTranslations,
      authRegisterPageUrl,
      handleClose,
    });

  const dialogRef = useRef<HTMLDivElement>(null);
  const isReady = !isCheckingUser && !isInitializing;

  const stopPropagation = useCallback((event: MouseEvent) => event.stopPropagation(), []);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    dialogRef.current?.focus();

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isReady, handleClose]);

  if (!isReady) {
    return null;
  }

  return (
    <div className={styles.wrapper} role="presentation" onClick={stopPropagation}>
      <div
        ref={dialogRef}
        className={styles.overlay}
        role="dialog"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        data-testid="video-feedback-overlay"
        aria-live="polite"
        aria-atomic="true"
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label={__("common.close")}
          data-testid="video-feedback-close"
        >
          <UiCrossIcon width={24} height={24} className={styles.closeButtonIcon} aria-hidden="true" />
        </button>
        {status === ContentFeedbackStatus.JustSubmitted && (
          <div className={styles.content}>
            <p className={styles.title} id={TITLE_ID}>
              {submittedMessage}
            </p>
            {submittedRewardHtml && (
              <p className={styles.subtitle} dangerouslySetInnerHTML={{ __html: sanitize(submittedRewardHtml) }} />
            )}
          </div>
        )}

        {status !== ContentFeedbackStatus.JustSubmitted &&
          (message ? (
            <div className={styles.content}>
              <p className={styles.title} id={TITLE_ID}>
                {message}
              </p>
            </div>
          ) : (
            <QuestionWithActions question={question} actions={actions} />
          ))}
      </div>
    </div>
  );
};
